import clsx from 'clsx';
import React from 'react';
import { useNavigate, useLocation } from 'react-router';

export interface NavigationTabProps {
  to: string;
  label: string;
  onClick?: () => void;
  icon?: React.ElementType<{ fontSize?: number }>;
}

/**
 * A component that displays a tabbed navigation for the new backoffice.
 * It is used to navigate between the different sections of the new backoffice.
 * It is used in the navbar component.
 * It works in a similar way to the existing NavigationButton component.
 * It indicates the currently selected tab.
 */
export const NavigationTab = ({ to, label, onClick, icon: IconComponent }: NavigationTabProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Normalize paths by removing trailing slashes for comparison
  const normalizePath = (path: string) => path.replace(/\/$/, '');
  const normalizedPathname = normalizePath(location.pathname);
  const normalizedTo = normalizePath(to);

  // All buttons use exact path matching - normalize paths by removing trailing slashes
  const isActive = normalizedPathname === normalizedTo;

  const handleClick = (_e: React.MouseEvent) => {
    if (onClick) {
      onClick();
    } else if (to.startsWith('http')) {
      // External link - open in new tab
      window.open(to, '_blank', 'noopener,noreferrer');
    } else {
      // Internal link - use React Router navigation
      navigate(to);
    }
  };

  return (
    <button
      className={clsx(
        'px-2 py-2 rounded-md flex items-center gap-2 transition-colors',
        isActive ? 'bg-gray-200 text-gray-800 cursor-default' : 'hover:bg-gray-200 cursor-pointer',
      )}
      onClick={handleClick}
      tabIndex={isActive ? -1 : 0}
      aria-current={isActive ? 'page' : undefined}
      disabled={isActive}
    >
      {IconComponent && <IconComponent fontSize={20} />}
      <p>{label}</p>
    </button>
  );
};
